import { z } from 'zod';
/**
 * Begehungsbericht — das, was der Kunde nach der Begehung bekommt.
 *
 * Deckblatt mit der Marke des Büros, Zusammenfassung, je Mangel Beschreibung,
 * Schwere, Frist, betroffene Anlage, Status und ein QR-Code, über den der
 * Kunde die Behebung mit seinem Namen bestätigt. Der Code führt in denselben
 * Freimelde-Kanal wie das Etikett am Gerät (`/m/:token`) — nur gilt er hier
 * genau EINEM Mangel statt einer Anlage. So lässt sich auch ein Mangel ohne
 * Gerät bestätigen (verstellter Fluchtweg, fehlender Aushang).
 *
 * Der Bericht wird an der Begehung versioniert, nicht als `GeneratedDocument`:
 * dessen `projectId` ist Pflicht, und eine Begehung hat kein Projekt.
 */
/** Was der Server nach dem Erzeugen über den Bericht sagt — die Fassung existiert dann. */
export const begehungsberichtStatus = z.object({
    begehungId: z.number().int().positive(),
    berichtVersion: z.number().int().positive(),
    berichtErstelltAm: z.string().datetime(),
    /** Sprechender Dateiname dieser Fassung. */
    dateiname: z.string(),
    /** Wie viele Mängel der Bericht enthält. */
    anzahlMaengel: z.number().int().nonnegative(),
    /** Entwurfsvermerk, solange die Begehung nicht abgeschlossen ist. */
    entwurf: z.boolean(),
    /**
     * Was beim Erzeugen bemerkenswert war — z. B. ein ersetzter QR-Code, weil
     * der hinterlegte Klartext nach einem Schlüsselwechsel nicht mehr lesbar
     * war. Leer, wenn nichts zu sagen ist.
     */
    hinweise: z.array(z.string()),
});
/**
 * Stand des QR-Codes am Bericht je Mangel. `aktiv: false` heißt widerrufen:
 * Der gedruckte Code ist wertlos, bis „neu ausgeben" einen frischen liefert —
 * so lässt sich ein geleakter Bericht entwerten.
 */
export const mangelTagStatus = z.object({
    mangelId: z.number().int().positive(),
    aktiv: z.boolean(),
    createdAt: z.string().datetime(),
    widerrufenAm: z.string().datetime().nullable(),
});
/**
 * Versand per E-Mail. Empfängerzahl bewusst gedeckelt — dieselbe Grenze wie
 * beim Dokumentversand (Spam-Vektor über den eigenen Absender).
 */
export const begehungsberichtVersandRequest = z
    .object({
    empfaenger: z.array(z.string().trim().email()).min(1).max(10),
    betreff: z.string().trim().min(1).max(200),
    text: z.string().trim().min(1).max(5000),
})
    .strict();
export const begehungsberichtVersandAntwort = z.object({
    gesendet: z.literal(true),
    empfaenger: z.array(z.string()),
    messageId: z.string().nullable(),
    berichtVersion: z.number().int().positive(),
});
/**
 * KI-Entwurf für Zusammenfassung und Fazit.
 *
 * `verfuegbar: false` ist KEIN Fehler: Ohne konfigurierten Anbieter oder bei
 * einer Störung schreibt der Planer die Texte selbst — der Bericht hängt
 * nicht an der KI. Deshalb antwortet der Endpunkt nie mit 5xx.
 */
export const zusammenfassungVorschlag = z.discriminatedUnion('verfuegbar', [
    z.object({
        verfuegbar: z.literal(true),
        zusammenfassung: z.string(),
        fazit: z.string(),
        model: z.string(),
    }),
    z.object({
        verfuegbar: z.literal(false),
        hinweis: z.string(),
    }),
]);
/**
 * Etikettenstand je Anlage — für die Druckansicht.
 *
 * `druckbar: false` heißt: Es gibt ein aktives Etikett, aber sein Klartext ist
 * nicht hinterlegt (ausgegeben, bevor der Nachdruck möglich war). Es gilt
 * weiter; wer es drucken will, muss es neu ausgeben.
 */
export const assetTagStatus = z.object({
    anlageId: z.number().int().positive(),
    erstelltAm: z.string().datetime(),
    druckbar: z.boolean(),
});
/** Raster der Etikettenseite (A4). Zentral, damit Server und Hilfe dasselbe sagen. */
export const ETIKETTEN_RASTER = { spalten: 3, zeilen: 8 };
/**
 * Beide Wege sind gleichberechtigt — der Kunde meldet am Bericht oder am
 * Gerät, wie es für ihn gerade praktisch ist.
 */
export const BERICHT_QR_HINWEIS = 'Mit dem QR-Code neben dem Mangel bestätigen Sie dessen Behebung — genauso mit dem QR-Etikett am Gerät, wenn eines angebracht ist. Die Meldung wird bei der nächsten Kontrolle geprüft und erst dann bestätigt.';
